import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Skeleton } from '@mui/material';
import type { InvoiceOverview } from '@vy/api-definition';
import { getSelectorAttribute, invoicesOverviewPageSelectors } from '@vy/app-selectors';
import { INVOICE_PDF_TYPES } from '@vy/types';
import { validateNotNullOrUndefined } from '@vy/utils';
import React from 'react';
import { useConnectedIntegrations } from '../../../../../hooks/requests/use-integrations';
import { useUsageEventsReportConfiguration } from '../../../../../hooks/requests/use-usage-events-report-configuration';
import { useActivate } from '../../../../../hooks/use-activate';
import { useSwallowClick } from '../../../../../hooks/use-swallow-click';
import { InvoicesActionsMenu } from '../invoices-actions-menu/InvoicesActionsMenu';
import { ApplyCreditDialog } from '../../../invoices-components/apply-credit-dialog/ApplyCreditDialog';
import { MarkAsPaidDialog } from '../MarkAsPaidDialog/MarkAsPaidDialog';
import { SyncInvoicesAsSalesOrderDialog } from '../SyncInvoicesAsSalesOrderDialog/SyncInvoicesAsSalesOrderDialog';
import { useInvoiceActions } from '../../../hooks/use-invoice-actions';

type InvoiceOverviewTableDotMenuProps = {
  invoice: InvoiceOverview;
};

export const InvoiceOverviewTableDotMenu: React.FC<InvoiceOverviewTableDotMenuProps> = ({ invoice }) => {
  const [isMarkAsPaidDialogOpen, setMarkAsPaidDialogOpen, setMarkAsPaidDialogClose] = useActivate(false);
  const [isApplyCreditOpen, setApplyCreditOpen, setApplyCreditClose] = useActivate(false);
  const swallowClick = useSwallowClick();
  const {
    isInvoiceApplicableForSync,
    downloadInvoicePdf,
    downloadUsageEventsReport,
    syncToNetSuite,
    syncToNetSuiteSalesOrder,
    confirmSyncToNetSuiteSalesOrder,
    isSalesOrderConfirmDialogOpen,
    handleCloseSalesOrderConfirmDialog,
    syncToStripe,
    approveSelectedInvoice,
    refund,
    grantCredit,
    handleSyncToQuickBooks,
    handleSyncToMorning,
    handleSyncToMarketplace,
    handleSyncToXero,
    handleSyncToPaddle,
  } = useInvoiceActions(invoice);

  const { data: connectedIntegrations, isLoading } = useConnectedIntegrations();
  const { data: reportConfig } = useUsageEventsReportConfiguration();
  const hasUsageEventsReportConfiguration = reportConfig?.configured ?? false;

  if (isLoading) {
    return <Skeleton variant="rectangular" sx={{ width: '100px', height: '10px', borderRadius: '12px' }} />;
  }

  validateNotNullOrUndefined(connectedIntegrations);

  return (
    <div
      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
      }}
    >
      {isMarkAsPaidDialogOpen && (
        <MarkAsPaidDialog
          open={isMarkAsPaidDialogOpen}
          invoiceId={invoice.id}
          onClose={() => setMarkAsPaidDialogClose()}
          customerName={invoice.customer.name}
          customerId={invoice.customer.id}
          currency={invoice.currency}
        />
      )}

      <div onClick={swallowClick} className="z-[9999] flex justify-center">
        <InvoicesActionsMenu
          invoices={[invoice]}
          contractName={invoice.contractName ?? ''}
          onDownloadAsPdf={() => downloadInvoicePdf(INVOICE_PDF_TYPES.Light)}
          onDownloadAsPdfStatement={() => downloadInvoicePdf(INVOICE_PDF_TYPES.Statement)}
          onDownloadUsageEventsReport={downloadUsageEventsReport}
          hasUsageEventsReportConfiguration={hasUsageEventsReportConfiguration}
          onSyncToNetSuite={syncToNetSuite}
          onSyncToNetSuiteSalesOrder={syncToNetSuiteSalesOrder}
          onSyncToStripe={syncToStripe}
          onSyncToQuickBooks={handleSyncToQuickBooks}
          onSyncToMorning={handleSyncToMorning}
          onSyncToMarketplace={handleSyncToMarketplace}
          onSyncToXero={handleSyncToXero}
          onSyncToPaddle={handleSyncToPaddle}
          onApproveInvoices={approveSelectedInvoice}
          connectedIntegrations={connectedIntegrations}
          syncToNetSuiteDisabled={!isInvoiceApplicableForSync}
          onMarkAsPaid={() => setMarkAsPaidDialogOpen()}
          onRefund={refund}
          onGrantCredits={() =>
            grantCredit({
              invoiceId: invoice.id,
              contractId: invoice.contractId,
              customerId: invoice.customer.id,
            })
          }
          onApplyCredit={() => setApplyCreditOpen()}
          disableRefund={false}
          disableCreditGrant={false}
        >
          <IconButton {...getSelectorAttribute(invoicesOverviewPageSelectors.dotsMenu.dotOptions)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </InvoicesActionsMenu>

        <SyncInvoicesAsSalesOrderDialog
          open={isSalesOrderConfirmDialogOpen}
          onClose={handleCloseSalesOrderConfirmDialog}
          invoices={[invoice]}
          onSyncInvoices={confirmSyncToNetSuiteSalesOrder}
        />

        {isApplyCreditOpen && (
          <ApplyCreditDialog
            open={isApplyCreditOpen}
            onClose={() => setApplyCreditClose()}
            invoiceId={invoice.id}
            invoiceName={invoice.name}
            customerId={invoice.customer.id}
            contractId={invoice.contractId}
            maxAmount={invoice.revenueBreakdown?.total ?? 0}
            currency={invoice.currency}
          />
        )}
      </div>
    </div>
  );
};
